import { MapStorage } from './MapStorage';
import { MapDefinition } from './MapTypes';
import { MapPlayScene, DifficultyLevel } from '../play/MapPlayScene';

interface LoadMapSceneCallbacks {
  onBack?: () => void;
  onEditor?: () => void;
}

export class LoadMapScene {
  private root: HTMLDivElement;
  private mapsList: HTMLDivElement;
  private playScene: MapPlayScene | null = null;
  private callbacks: LoadMapSceneCallbacks;

  constructor(callbacks: LoadMapSceneCallbacks = {}) {
    this.callbacks = callbacks;
    this.root = document.createElement('div');
    this.root.className = 'load-map-scene';
    this.root.style.display = 'none';
    this.ensureStyles();
    this.buildLayout();
    document.body.appendChild(this.root);
  }

  show(): void {
    this.root.style.display = 'flex';
    this.refreshMapsList();
  }

  hide(): void {
    this.root.style.display = 'none';
    if (this.playScene) {
      this.playScene.hide();
      this.playScene = null;
    }
  }

  private ensureStyles(): void {
    if (document.getElementById('load-map-scene-styles')) return;
    const style = document.createElement('style');
    style.id = 'load-map-scene-styles';
    style.textContent = `
      .load-map-scene {
        position: fixed;
        inset: 0;
        background: rgba(5, 5, 10, 0.95);
        color: #fff;
        font-family: 'Courier New', monospace;
        z-index: 10000;
        display: flex;
        flex-direction: column;
        padding: 40px;
      }
      .load-map-header {
        text-align: center;
        margin-bottom: 30px;
      }
      .load-map-title {
        font-size: 42px;
        font-weight: bold;
        margin-bottom: 10px;
      }
      .load-map-content {
        flex: 1;
        display: flex;
        gap: 20px;
        max-width: 1400px;
        margin: 0 auto;
        width: 100%;
      }
      .load-map-list {
        flex: 1;
        background: rgba(20, 20, 30, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        padding: 20px;
        overflow-y: auto;
        max-height: calc(100vh - 200px);
      }
      .load-map-item {
        background: rgba(30, 30, 40, 0.8);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 6px;
        padding: 16px;
        margin-bottom: 12px;
        cursor: pointer;
        transition: all 0.2s;
      }
      .load-map-item:hover {
        background: rgba(40, 40, 50, 0.9);
        border-color: rgba(255, 255, 255, 0.2);
      }
      .load-map-item-name {
        font-size: 23px;
        font-weight: bold;
        margin-bottom: 6px;
        color: #ffd700;
      }
      .load-map-item-meta {
        font-size: 16px;
        color: #aaa;
        margin-bottom: 8px;
      }
      .load-map-item-actions {
        display: flex;
        gap: 8px;
        margin-top: 12px;
      }
      .load-map-btn {
        background: #2a4a2a;
        border: 1px solid #4a6a4a;
        color: #fff;
        padding: 6px 12px;
        font-family: 'Courier New', monospace;
        font-size: 17px;
        border-radius: 4px;
        cursor: pointer;
      }
      .load-map-btn:hover {
        background: #3a6a3a;
      }
      .load-map-btn-primary {
        background: #00a86b;
        border-color: #00c87a;
      }
      .load-map-btn-primary:hover {
        background: #00c87a;
      }
      .load-map-btn-danger {
        background: #6a2a2a;
        border-color: #8a4a4a;
      }
      .load-map-btn-danger:hover {
        background: #8a4a4a;
      }
      .load-map-empty {
        text-align: center;
        color: #666;
        padding: 40px;
        font-size: 16px;
      }
      .load-map-sidebar {
        width: 300px;
        background: rgba(20, 20, 30, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .load-map-sidebar-btn {
        background: #252525;
        border: 1px solid #555;
        color: #fff;
        padding: 12px;
        font-family: 'Courier New', monospace;
        font-size: 18px;
        border-radius: 6px;
        cursor: pointer;
        text-align: center;
      }
      .load-map-sidebar-btn:hover {
        background: #333;
      }
      .load-map-sidebar-btn-primary {
        background: #00a86b;
        border-color: #00c87a;
        font-weight: bold;
      }
      .load-map-sidebar-btn-primary:hover {
        background: #00c87a;
      }
    `;
    document.head.appendChild(style);
  }

  private buildLayout(): void {
    const header = document.createElement('div');
    header.className = 'load-map-header';
    const title = document.createElement('div');
    title.className = 'load-map-title';
    title.textContent = 'Load Map';
    header.appendChild(title);
    this.root.appendChild(header);

    const content = document.createElement('div');
    content.className = 'load-map-content';

    this.mapsList = document.createElement('div');
    this.mapsList.className = 'load-map-list';
    content.appendChild(this.mapsList);

    const sidebar = document.createElement('div');
    sidebar.className = 'load-map-sidebar';
    
    const editorBtn = document.createElement('button');
    editorBtn.className = 'load-map-sidebar-btn';
    editorBtn.textContent = 'Open Editor';
    editorBtn.onclick = () => {
      this.hide();
      this.callbacks.onEditor?.();
    };
    sidebar.appendChild(editorBtn);

    const backBtn = document.createElement('button');
    backBtn.className = 'load-map-sidebar-btn';
    backBtn.textContent = '← Back to Menu';
    backBtn.onclick = () => {
      this.hide();
      this.callbacks.onBack?.();
    };
    sidebar.appendChild(backBtn);

    content.appendChild(sidebar);
    this.root.appendChild(content);
  }

  private refreshMapsList(): void {
    this.mapsList.innerHTML = '';
    
    const defaultMaps = MapStorage.listDefaults();
    const userMaps = MapStorage.listUserMaps();
    const allMaps = [...defaultMaps, ...userMaps];
    
    if (allMaps.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'load-map-empty';
      empty.textContent = 'No saved maps. Create one in the Editor!';
      this.mapsList.appendChild(empty);
      return;
    }

    // Show default maps section
    if (defaultMaps.length > 0) {
      const defaultSection = document.createElement('div');
      defaultSection.style.cssText = 'margin-bottom: 20px; padding-bottom: 20px; border-bottom: 2px solid rgba(255, 255, 255, 0.2);';
      
      const defaultHeader = document.createElement('div');
      defaultHeader.style.cssText = 'font-size: 24px; font-weight: bold; color: #ffd700; margin-bottom: 12px;';
      defaultHeader.textContent = 'Default Maps';
      defaultSection.appendChild(defaultHeader);
      
      for (const map of defaultMaps) {
        const item = this.createMapItem(map, true);
        defaultSection.appendChild(item);
      }
      
      this.mapsList.appendChild(defaultSection);
    }

    // Show user maps section
    if (userMaps.length > 0) {
      const userSection = document.createElement('div');
      
      if (defaultMaps.length > 0) {
        const userHeader = document.createElement('div');
        userHeader.style.cssText = 'font-size: 24px; font-weight: bold; color: #88ccff; margin-bottom: 12px; margin-top: 20px;';
        userHeader.textContent = 'Your Maps';
        userSection.appendChild(userHeader);
      }
      
      for (const map of userMaps) {
        const item = this.createMapItem(map, false);
        userSection.appendChild(item);
      }
      
      this.mapsList.appendChild(userSection);
    }
  }

  private createMapItem(map: MapDefinition, isDefault: boolean): HTMLDivElement {
    const item = document.createElement('div');
    item.className = 'load-map-item';
    if (isDefault) {
      item.style.borderColor = 'rgba(255, 215, 0, 0.3)';
    }

    const name = document.createElement('div');
    name.className = 'load-map-item-name';
    name.textContent = map.name || 'Unnamed Map';
    if (isDefault) {
      const badge = document.createElement('span');
      badge.style.cssText = 'font-size: 14px; color: #ffd700; margin-left: 8px;';
      badge.textContent = '[Default]';
      name.appendChild(badge);
    }
    item.appendChild(name);

    const meta = document.createElement('div');
    meta.className = 'load-map-item-meta';
    meta.textContent = `By ${map.author || 'Unknown'} • ${map.spawns.length} spawn${map.spawns.length !== 1 ? 's' : ''} • ${map.difficultyTags?.join(', ') || 'No tags'}`;
    item.appendChild(meta);

    if (map.metadata.description) {
      const desc = document.createElement('div');
      desc.style.cssText = 'font-size: 16px; color: #888; margin-top: 6px;';
      desc.textContent = map.metadata.description;
      item.appendChild(desc);
    }

    const actions = document.createElement('div');
    actions.className = 'load-map-item-actions';

    const playBtn = document.createElement('button');
    playBtn.className = 'load-map-btn load-map-btn-primary';
    playBtn.textContent = 'Play';
    playBtn.onclick = (e) => {
      e.stopPropagation();
      this.playMap(map);
    };
    actions.appendChild(playBtn);

    // Only show delete button for user maps
    if (!isDefault) {
      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'load-map-btn load-map-btn-danger';
      deleteBtn.textContent = 'Delete';
      deleteBtn.onclick = (e) => {
        e.stopPropagation();
        if (confirm(`Delete "${map.name || 'Unnamed Map'}"?`)) {
          MapStorage.delete(map.id);
          this.refreshMapsList();
        }
      };
      actions.appendChild(deleteBtn);
    }

    item.appendChild(actions);
    return item;
  }

  private playMap(map: MapDefinition): void {
    this.hide();
    this.playScene = new MapPlayScene(map, {
      onBack: () => {
        if (this.playScene) {
          this.playScene.hide();
          this.playScene = null;
        }
        this.show();
      },
    });
    this.playScene.show();
  }
}

